import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Copy, Loader2, ScanText, X } from "lucide-react";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";
import { dapi as api } from "./deskApi";

// One still frame of this browser tab (Chrome asks permission each time).
async function grabTab() {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: "browser" }, audio: false, preferCurrentTab: true, selfBrowserSurface: "include",
  });
  try {
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await new Promise((r) => setTimeout(r, 250));
    const c = document.createElement("canvas");
    c.width = video.videoWidth; c.height = video.videoHeight;
    c.getContext("2d").drawImage(video, 0, 0);
    return c;
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

const Bar = ({ children }) => (
  <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[10001] flex items-center gap-1 rounded-lg bg-white shadow-2xl border border-slate-200 px-2 py-1.5 text-sm text-slate-800" data-testid="snip-toolbar">
    {children}
  </div>
);

// Drag a box anywhere on the screen; the AI reads the words and you can select or copy them.
export default function SnipTool({ onClose }) {
  const [shot, setShot] = useState(null); // {url, canvas, sx, sy}
  const [box, setBox] = useState(null);
  const [result, setResult] = useState(null); // {lines, box}
  const [busy, setBusy] = useState(false);
  const start = useRef(null);
  // The parent passes a new onClose on every render; keep the latest in a ref so the screen is grabbed only once.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const grabbing = useRef(false);
  const rootRef = useRef(null);

  const capture = useCallback(async () => {
    if (grabbing.current) return;
    grabbing.current = true;
    document.activeElement?.blur?.(); // take keyboard focus back from a program window so Esc works
    setBox(null); setResult(null);
    try {
      const canvas = await grabTab();
      setShot({ url: canvas.toDataURL("image/png"), canvas, sx: canvas.width / window.innerWidth, sy: canvas.height / window.innerHeight });
    } catch {
      toast.message("Snipping Tool needs permission to see this tab — choose this tab and click Share.");
      closeRef.current();
    } finally { grabbing.current = false; }
  }, []);
  useEffect(() => { capture(); }, [capture]);
  useEffect(() => { if (shot) rootRef.current?.focus(); }, [shot]);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") closeRef.current(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const read = async (b) => {
    const c = document.createElement("canvas");
    c.width = Math.round(b.w * shot.sx); c.height = Math.round(b.h * shot.sy);
    c.getContext("2d").drawImage(shot.canvas, b.x * shot.sx, b.y * shot.sy, c.width, c.height, 0, 0, c.width, c.height);
    setBusy(true);
    try {
      const { data } = await api.post("/desktop/snip-text", { image: c.toDataURL("image/png") }, { timeout: 150000 });
      setResult({ lines: data.lines, scale: b.w / data.width });
      if (!data.lines.length) toast.message("No words found in that area.");
    } catch (e) {
      toast.error(e.response ? formatErr(e.response.data?.detail) : "The connection dropped. Please try again.");
      setBox(null);
    } finally { setBusy(false); }
  };
  const down = (e) => { if (result || busy) return; start.current = { x: e.clientX, y: e.clientY }; setBox({ x: e.clientX, y: e.clientY, w: 0, h: 0 }); };
  const move = (e) => {
    if (!start.current) return;
    const s = start.current;
    setBox({ x: Math.min(s.x, e.clientX), y: Math.min(s.y, e.clientY), w: Math.abs(e.clientX - s.x), h: Math.abs(e.clientY - s.y) });
  };
  const up = () => {
    if (!start.current) return;
    start.current = null;
    if (box && box.w > 8 && box.h > 8) read(box); else setBox(null);
  };
  const copyAll = async () => {
    const text = result.lines.map((l) => l.text).join("\n");
    try { await navigator.clipboard.writeText(text); toast.success("Text copied."); } catch { toast.error("Couldn't copy — select the text and press Ctrl+C."); }
  };

  if (!shot) return null;
  return (
    <div ref={rootRef} tabIndex={-1} className="fixed inset-0 z-[10000] select-none cursor-crosshair outline-none" onPointerDown={down} onPointerMove={move} onPointerUp={up} data-testid="snip-overlay">
      <img src={shot.url} alt="" draggable={false} className="absolute inset-0 w-full h-full" />
      <div className="absolute inset-0 bg-black/45" style={box ? { clipPath: `polygon(0 0,100% 0,100% 100%,0 100%,0 ${box.y}px,${box.x}px ${box.y}px,${box.x}px ${box.y + box.h}px,${box.x + box.w}px ${box.y + box.h}px,${box.x + box.w}px ${box.y}px,0 ${box.y}px)` } : undefined} />
      {box && (
        <div className="absolute border-2 border-dashed border-white" style={{ left: box.x, top: box.y, width: box.w, height: box.h }} data-testid="snip-box">
          {result && result.lines.map((l) => (
            <span key={l.id} className="absolute select-text cursor-text text-transparent selection:bg-sky-400/50 selection:text-transparent whitespace-pre leading-none hover:bg-sky-300/20"
              style={{ left: l.box[0] * result.scale, top: l.box[1] * result.scale, width: (l.box[2] - l.box[0]) * result.scale, height: (l.box[3] - l.box[1]) * result.scale, fontSize: (l.box[3] - l.box[1]) * result.scale * 0.85 }}
              onPointerDown={(e) => e.stopPropagation()} data-testid={`snip-line-${l.id}`}>{l.text}</span>
          ))}
        </div>
      )}
      <Bar>
        {!box && <span className="flex items-center gap-2 px-2"><ScanText className="w-4 h-4 text-sky-600" /> Drag a box around the words</span>}
        {busy && <span className="flex items-center gap-2 px-2" data-testid="snip-reading"><Loader2 className="w-4 h-4 animate-spin" /> Reading the words…</span>}
        {result && <>
          <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={() => { setBox(null); setResult(null); }} className="p-1.5 rounded hover:bg-slate-100" title="New snip" data-testid="snip-again"><ArrowLeft className="w-4 h-4" /></button>
          <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={copyAll} disabled={!result.lines.length}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded hover:bg-slate-100 font-medium disabled:opacity-40" data-testid="snip-copy-all"><Copy className="w-4 h-4" /> Copy all text</button>
        </>}
        <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={onClose} className="p-1.5 rounded hover:bg-slate-100" title="Close (Esc)" data-testid="snip-close"><X className="w-4 h-4" /></button>
      </Bar>
    </div>
  );
}
